/**
 * Production verification shell navigation.
 *
 * Derived from the archived verification router chrome for WP1a (#46597).
 * HG12: TanStack owns route-per-page registration (WP1b).
 * This module is the rail model and pathname highlighter only — it does not
 * resolve a splat remainder or render pages.
 *
 * Canonical launch route is `/` (HG1). Badge counts are not baked in;
 * the shell accepts live counts as props so product mode stays server-sourced.
 */

import { CANONICAL_NAV_GROUPS } from "./navigation/canonical";
import type { IconName } from "./icons";
export { preserveEnvironmentHref } from "./environment-navigation";

/** Product mount. Every in-app href is built from this. */
export const BASE = "/";

export type PageId =
  | "overview"
  | "inbox"
  | "queue"
  | "work"
  | "work-detail"
  | "finding"
  | "findings"
  | "runs"
  | "run"
  | "workflows"
  | "workflow"
  | "controls"
  | "control"
  | "policies"
  | "policy"
  | "evidence"
  | "artifact"
  | "connectors"
  | "environments"
  | "outcomes"
  | "autonomy"
  | "analytics"
  | "receipts"
  | "receipt"
  | "packs"
  | "pack"
  | "audit-room"
  | "workers"
  | "scorecard"
  | "runtime"
  | "api"
  | "demo-lab"
  | "workflow-new"
  | "trust"
  | "posture"
  | "buyable"
  | "guide"
  | "design"
  | "settings"
  | "not-found";

export interface NavItem {
  page: PageId;
  path: string;
  label: string;
  icon: IconName;
  /** One-line purpose — shown as the nav tooltip. */
  purpose: string;
  countTone?: "fail" | "unknown" | "accent";
  /** Detail pages that should light this rail row up once WP1b registers them. */
  alsoActiveFor?: readonly PageId[];
}

export interface NavGroup {
  label: string | null;
  /** Why this group exists. */
  rationale: string;
  items: readonly NavItem[];
}

export const LEGACY_VERIFICATION_NAV_ARCHIVE: NavGroup[] = [
  {
    label: null,
    rationale:
      "The one screen that answers 'could we hand an auditor a pack today?'",
    items: [
      {
        page: "overview",
        path: "",
        label: "Overview",
        icon: "gauge",
        purpose:
          "Trust posture for the period: control verdicts, what blocks a pack, what changed today.",
      },
    ],
  },
  {
    label: "Decide",
    rationale:
      "The human decision surface. First in the rail because it is opened many times a day, whereas Define is opened once at setup.",
    items: [
      {
        page: "queue",
        path: "queue",
        label: "Decision inbox",
        icon: "inbox",
        purpose:
          "Findings waiting on a decision from you, worst-first. The default landing place for a decision owner.",
        countTone: "accent",
      },
      {
        page: "findings",
        path: "findings",
        label: "Findings",
        icon: "flag",
        purpose:
          "Every FAIL and UNKNOWN across the tenant, filterable by control, owner, severity and age.",
        countTone: "fail",
        alsoActiveFor: ["finding"],
      },
      {
        page: "runs",
        path: "runs",
        label: "Runs",
        icon: "play",
        purpose:
          "Verification run history. A run detail page is the full story: claim, checks, evidence, receipt, decisions.",
        alsoActiveFor: ["run"],
      },
    ],
  },
  {
    label: "Define",
    rationale:
      "What must be true, and how the engine is allowed to decide it. Set up once, revised deliberately.",
    items: [
      {
        page: "workflows",
        path: "workflows",
        label: "Workflows",
        icon: "link",
        purpose:
          "Connect a real workflow — human or agent — by naming an independent oracle. Nothing is admitted without one.",
        countTone: "unknown",
        alsoActiveFor: ["workflow", "workflow-new"],
      },
      {
        page: "controls",
        path: "controls",
        label: "Controls",
        icon: "shield",
        purpose:
          "The control library and its requirements: owner, frequency, population, current verdict.",
        alsoActiveFor: ["control"],
      },
      {
        page: "policies",
        path: "policies",
        label: "Policies",
        icon: "file",
        purpose:
          "The checkable logic behind each control, versioned. Loosening a policy needs impact analysis and a second human.",
        countTone: "unknown",
        alsoActiveFor: ["policy"],
      },
    ],
  },
  {
    label: "Evidence",
    rationale:
      "Where the facts come from. Separated from Define because sources fail on their own schedule.",
    items: [
      {
        page: "evidence",
        path: "evidence",
        label: "Evidence",
        icon: "book",
        purpose:
          "Content-addressed artifacts and the observations derived from them, with full lineage.",
        alsoActiveFor: ["artifact"],
      },
      {
        page: "connectors",
        path: "connectors",
        label: "Connectors",
        icon: "plug",
        purpose:
          "Read-only source connections, their scopes, cadence and freshness. A stale source is why a control is UNKNOWN.",
        countTone: "fail",
      },
    ],
  },
  {
    label: "Prove",
    rationale:
      "The output the customer actually buys: something an outside party can check without trusting us.",
    items: [
      {
        page: "receipts",
        path: "receipts",
        label: "Receipts",
        icon: "seal",
        purpose:
          "The append-only ledger binding each verdict to its evidence, policy version and signing key.",
        alsoActiveFor: ["receipt"],
      },
      {
        page: "packs",
        path: "packs",
        label: "Evidence packs",
        icon: "package",
        purpose:
          "Scoped metadata exports with recorded hashes and limitations in the open.",
        alsoActiveFor: ["pack"],
      },
      {
        page: "audit-room",
        path: "audit-room",
        label: "Audit room",
        icon: "door",
        purpose:
          "Time-limited read-only access for an external reviewer, with every view and download logged.",
      },
    ],
  },
  {
    label: "Operate",
    rationale:
      "The machinery's own health. Read-only for a reviewer; the operator's home.",
    items: [
      {
        page: "workers",
        path: "workers",
        label: "Semantic workers",
        icon: "cpu",
        purpose:
          "Model-based checkers, their calibration and boundary. A model may never issue an authoritative PASS.",
      },
      {
        page: "scorecard",
        path: "scorecard",
        label: "Scorecard",
        icon: "chart",
        purpose:
          "The nine metrics that decide whether this workspace's verification is trustworthy enough to sell on.",
      },
      {
        page: "runtime",
        path: "runtime",
        label: "Runtime & keys",
        icon: "server",
        purpose:
          "Where evidence lives, what key metadata is reported, and whether cryptographic verification is available.",
      },
      {
        page: "api",
        path: "sdk",
        label: "API & SDK",
        icon: "code",
        purpose:
          "The served endpoints, their scopes, and the one operation deliberately withheld from them.",
      },
      {
        page: "trust",
        path: "trust",
        label: "Trust root",
        icon: "shield",
        purpose:
          "Out-of-band keys and chain anchoring: what an outside party can check without trusting us, and where that still has a ceiling.",
      },
      {
        page: "posture",
        path: "posture",
        label: "Security posture",
        icon: "lock",
        purpose:
          "The enterprise security questionnaire, answered in the product. Unanswered rows are shown as unanswered.",
      },
    ],
  },
  {
    label: "Reference",
    rationale: "Documentation, in the product rather than beside it.",
    items: [
      {
        page: "guide",
        path: "guide",
        label: "How it works",
        icon: "book",
        purpose:
          "The product explained end to end: the loop, the vocabulary, the jobs, what is built and what is not.",
      },
      {
        page: "buyable",
        path: "buyable",
        label: "Is it buyable?",
        icon: "check",
        purpose:
          "The five things a stranger must be able to do, each with the gate that protects it and the ceiling on its claim.",
      },
      {
        page: "design",
        path: "design",
        label: "Design system",
        icon: "code",
        purpose: "The verification shell tokens, primitives and rules.",
      },
    ],
  },
];

export const NAV: readonly NavGroup[] = CANONICAL_NAV_GROUPS;

/** Flat list, for lookups. */
export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/** Build an in-app href. `href()` is the product index at `/`. */
export function href(...segments: string[]): string {
  const parts = segments
    .map((s) => s.replace(/^\/+|\/+$/g, ""))
    .filter(Boolean);
  return parts.length === 0 ? BASE : `${BASE}${parts.join("/")}`;
}

export function pathOf(item: NavItem): string {
  return href(...(item.path ? [item.path] : []));
}

/** Which rail row should read as active for a resolved page id. */
export function activeNavPage(page: PageId): PageId | null {
  for (const item of NAV_ITEMS) {
    if (item.page === page) return item.page;
    if (item.alsoActiveFor?.includes(page)) return item.page;
  }
  return null;
}

/**
 * Highlight the rail from the TanStack pathname (HG12). Longest prefix wins so
 * `/findings/:id` lights Findings rather than Overview. This is not a splat
 * page resolver — WP1b registers routes; the shell only paints chrome.
 */
export function activeNavFromPathname(pathname: string): PageId | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/") return "overview";
  if (normalized === "/settings") return null;
  let best: { page: PageId; len: number } | null = null;
  for (const item of NAV_ITEMS) {
    const p = pathOf(item);
    if (p === "/") continue;
    if (normalized === p || normalized.startsWith(`${p}/`)) {
      if (!best || p.length > best.len)
        best = { page: item.page, len: p.length };
    }
  }
  return best?.page ?? null;
}

/** The rail row for a page, so breadcrumbs can name its section. */
export function navItemFor(page: PageId): NavItem | undefined {
  const active = activeNavPage(page);
  return NAV_ITEMS.find((i) => i.page === active);
}

/** The group label a page sits under. */
export function groupFor(page: PageId): string | null {
  const active = activeNavPage(page);
  for (const g of NAV) {
    if (g.items.some((i) => i.page === active)) return g.label;
  }
  return null;
}

/** Chrome route from the live pathname. Detail ids are the second segment. */
export function chromeRoute(pathname: string): { page: PageId; id?: string } {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/settings") return { page: "settings" };
  const parts = normalized.split("/").filter(Boolean);
  const page = activeNavFromPathname(normalized) ?? "overview";
  if (parts.length >= 2 && parts[1] && parts[1] !== "new") {
    return { page, id: parts[1] };
  }
  return { page };
}
